/**
 * Starts spoofing/impersonation for the given username.
 *
 * @param {string} targetUsername - The user to impersonate.
 * @returns {Promise<{ success: true } | { success: false, error: string }>}
 */
export async function startSpoofing(targetUsername) {
  const currentAuthToken = sessionStorage.getItem("authSpoofer") || sessionStorage.getItem("auth");
  if (!currentAuthToken) {
    return { success: false, error: "No current auth token found" };
  }

  await stopSpoofing();
  try {
    const response = await fetch(`/api/spoofing/start`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentAuthToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ targetUsername }) // 👈 Send as JSON body
    });

    if (!response.ok) {
      const errMsg = await response.text();
      return { success: false, error: `Spoofing failed: ${errMsg}` };
    }

    const result = await response.json();

    clearSessionStorage();
    populateSessionStorageForSpoofing(result, currentAuthToken);

    return { success: true };

  } catch (error) {
    console.error("Unexpected error during spoofing:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
}


/**
 * Stops the current spoofing/impersonation session.
 *
 * @returns {Promise<{ success: true } | { success: false, error: string }>}
 */
export async function stopSpoofing() {
  const spooferAuthToken = sessionStorage.getItem("authSpoofer");
  const targetAuthToken = sessionStorage.getItem("auth");

  if (!spooferAuthToken || !targetAuthToken) {
    return { success: false, error: "Missing spoofing tokens in sessionStorage" };
  }

  try {
    const response = await fetch(`/api/spoofing/stop`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${spooferAuthToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        spooferAuthToken,
        targetAuthToken
      })
    });

    if (!response.ok) {
      const errMsg = await response.text();
      return { success: false, error: `Stop spoofing failed: ${errMsg}` };
    }

    const result = await response.json();

    clearSessionStorage();
    populateSessionStorageFromLoginDto(result);
    sessionStorage.removeItem("authSpoofer");

    return { success: true };

  } catch (error) {
    console.error("Unexpected error during stopSpoofing:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
}
/**
 * Populates sessionStorage using the fields from a LoginDto object.
 *
 * @param {Object} loginDto - The login session data.
 * @param {string} loginDto.Authentication - Auth token (bearer).
 * @param {boolean} loginDto.HasReports - Whether reports are available.
 * @param {string} loginDto.UserData - Base64-encoded user info.
 * @param {string} loginDto.Permissions - Permission payload.
 * @param {string} loginDto.Branding - Custom branding payload.
 * @param {string} loginDto.FF - Feature flags.
 * @param {string} loginDto.Nps - NPS tracking info.
 * @param {Array} loginDto.Gurus - List of guru objects.
 * @param {boolean} loginDto.FirstLogin - First-time login flag.
 * @param {boolean} loginDto.ShowWelcome - Whether to show welcome screen.
 * @param {boolean} loginDto.v3 - Feature version flag.
 */
export function populateSessionStorageFromLoginDto(loginDto) {
  sessionStorage.setItem("auth", loginDto.Authentication);
  sessionStorage.setItem("hasReports", loginDto.HasReports);
  sessionStorage.setItem("userData", loginDto.UserData);
  sessionStorage.setItem("permissions", loginDto.Permissions);
  sessionStorage.setItem("branding", loginDto.Branding);
  sessionStorage.setItem("ff", loginDto.FF);
  sessionStorage.setItem("nps", loginDto.Nps);
  sessionStorage.setItem("gurus", JSON.stringify(loginDto.Gurus));
  sessionStorage.setItem("firstLogin", loginDto.FirstLogin);
  sessionStorage.setItem("showWelcome", loginDto.ShowWelcome);
  sessionStorage.setItem("v3", loginDto.v3);
}

/**
 * Populates sessionStorage from a spoofing login response,
 * including storing the original spoofer's token.
 *
 * @param {Object} loginDto - The spoofed user's LoginDto.
 * @param {string} spooferToken - The token of the original user.
 */
export function populateSessionStorageForSpoofing(loginDto, spooferToken) {
  populateSessionStorageFromLoginDto(loginDto);
  sessionStorage.setItem("authSpoofer", spooferToken);
}

/**
 * Returns true if the session is currently spoofed.
 *
 * @returns {boolean}
 */
export function isSpoofingActive() {
  return !!sessionStorage.getItem("authSpoofer");
}

/**
 * Clears all sessionStorage data.
 */
export function clearSessionStorage() {
  sessionStorage.clear();
}

/**
 * Returns the current user's Bearer token from sessionStorage.
 *
 * @returns {string|null}
 */
export function getCurrentAuthToken() {
  return sessionStorage.getItem("auth");
}
